import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { companyAdminAPI } from '../../services/adminAPI';

const CompanyAdminDashboard = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('meetings'); // 'meetings', 'slots', 'buyers', or 'company-info'
  const [companyInfo, setCompanyInfo] = useState(null);
  const [descriptionData, setDescriptionData] = useState({
    description_1: '',
    description_2: '',
    description_3: '',
    company_description: ''
  });
  const [updating, setUpdating] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchMeetings();
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await companyAdminAPI.getCompanyMeetings();
      // Company bilgilerini meetings response'undan al
      if (response.data.company) {
        const company = response.data.company;
        setCompanyInfo(company);
        // Parse descriptions
        let desc1 = '', desc2 = '', desc3 = '';
        if (company.description) {
          try {
            const descObj = typeof company.description === 'string' ? JSON.parse(company.description) : company.description;
            desc1 = descObj.description_1 || descObj.field1 || '';
            desc2 = descObj.description_2 || descObj.field2 || '';
            desc3 = descObj.description_3 || descObj.field3 || '';
          } catch (e) {
            // Ignore
          }
        }
        setDescriptionData({
          description_1: desc1,
          description_2: desc2,
          description_3: desc3,
          company_description: company.company_description || ''
        });
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const handleUpdateDescriptions = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError('');
    try {
      await companyAdminAPI.updateCompanyDescription(descriptionData);
      setError('');
      alert('Company descriptions updated successfully!');
      fetchCompanyInfo();
    } catch (error) {
      setError(error.response?.data?.message || 'Error updating descriptions');
    } finally {
      setUpdating(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      const response = await companyAdminAPI.getCompanyMeetings();
      setMeetings(response.data.meetings);
      // Company bilgisini de kaydet (sent meetings filtrelemesi için)
      if (response.data.company) {
        setCompanyInfo(response.data.company);
      }
    } catch (error) {
      setError('Meetings Loading Error');
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    try {
      setSlotsLoading(true);
      const response = await companyAdminAPI.getCompanySlots();
      setSlots(response.data.slots);
    } catch (error) {
      setError('Slots Loading Error');
      console.error('Error fetching slots:', error);
    } finally {
      setSlotsLoading(false);
    }
  };

  const fetchBuyers = async () => {
    try {
      setBuyersLoading(true);
      const response = await companyAdminAPI.getBuyers();
      setBuyers(response.data.buyers);
    } catch (error) {
      setError('Buyers Loading Error');
      console.error('Error fetching buyers:', error);
    } finally {
      setBuyersLoading(false);
    }
  };

  const handleToggleSlot = async (slotId, currentAvailability) => {
    try {
      await companyAdminAPI.toggleSlotAvailability(slotId, !currentAvailability);
      await fetchSlots(); // Refresh slots
    } catch (error) {
      setError(error.response?.data?.message || 'Error toggling slot availability');
      console.error('Error toggling slot:', error);
    }
  };

  const handleUpdateStatus = async (meetingId, status) => {
    try {
      await companyAdminAPI.updateMeetingStatus(meetingId, status);
      fetchMeetings(); // Verileri yenile
      setError(''); // Başarılı olursa hata mesajını temizle
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Durum güncellenirken hata oluştu';
      setError(errorMessage);
      // Hata mesajını 5 saniye sonra temizle
      setTimeout(() => setError(''), 5000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ffc107';
      case 'approved':
        return '#28a745';
      case 'declined':
        return '#dc3545';
      default:
        return '#EDB404';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'declined':
        return 'Declined';
      default:
        return status;
    }
  };

  const formatTime = (time) => {
    return time.substring(0, 5);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR');
  };


  const openRequestModal = (buyer) => {
    navigate(`/request-meeting-from-buyer/${buyer.id}`);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Company Admin Dashboard</h1>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#EDB404',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Home
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#721c24',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        borderBottom: '2px solid #ddd'
      }}>
        <button
          onClick={() => {
            setActiveTab('meetings');
            fetchMeetings();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'meetings' ? '#EDB404' : 'transparent',
            color: activeTab === 'meetings' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'meetings' ? '3px solid #EDB404' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'meetings' ? 'bold' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          Meeting Requests
        </button>
        <button
          onClick={() => {
            setActiveTab('slots');
            fetchSlots();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'slots' ? '#EDB404' : 'transparent',
            color: activeTab === 'slots' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'slots' ? '3px solid #EDB404' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'slots' ? 'bold' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          Manage Time Slots
        </button>
        <button
          onClick={() => {
            setActiveTab('buyers');
            fetchBuyers();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'buyers' ? '#EDB404' : 'transparent',
            color: activeTab === 'buyers' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'buyers' ? '3px solid #EDB404' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'buyers' ? 'bold' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          Buyers
        </button>
        <button
          onClick={() => {
            setActiveTab('company-info');
            fetchCompanyInfo();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'company-info' ? '#EDB404' : 'transparent',
            color: activeTab === 'company-info' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'company-info' ? '3px solid #EDB404' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'company-info' ? 'bold' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          Company Info
        </button>
      </div>

      {activeTab === 'meetings' && (
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Incoming Meetings (from users/buyers) */}
        <div style={{ marginBottom: '40px' }}>
          <h2>Incoming Meeting Requests</h2>
          {(() => {
            // Gelen toplantılar: buyer_id yok VEYA (buyer_id var ama user_id company admin değil)
            const incomingMeetings = meetings.filter(meeting => {
              // Eğer buyer_id yoksa, bu normal user'dan gelen toplantı (gelen)
              if (!meeting.buyer_id) {
                return meeting.user_id !== user?.id;
              }
              // Eğer buyer_id varsa ama user_id company admin değilse, bu gelen toplantı
              return meeting.user_id !== user?.id;
            });
            return incomingMeetings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No incoming meeting requests yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {incomingMeetings.map((meeting) => (
              <div
                key={meeting.id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                      {meeting.buyer_id ? (meeting.buyer?.name || meeting.requester_name || 'Unknown') : (meeting.requester_name || meeting.user?.name || 'Unknown')} {meeting.buyer_id ? (meeting.buyer?.surname || meeting.requester_surname || '') : (meeting.requester_surname || '')}
                    </h4>
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Email:</strong> {meeting.buyer_id ? (meeting.buyer?.email || meeting.requester_email || 'N/A') : (meeting.requester_email || meeting.user?.email || 'N/A')}
                    </p>
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Phone:</strong> {meeting.buyer_id ? (meeting.buyer?.phone || 'N/A') : (meeting.user?.phone || 'N/A')}
                    </p>
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Date:</strong> {formatDate(meeting.meeting_slot.meeting_date)}
                    </p>
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Time:</strong> {formatTime(meeting.meeting_slot.start_time)} - {formatTime(meeting.meeting_slot.end_time)}
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                    <div
                      style={{
                        backgroundColor: getStatusColor(meeting.status),
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {getStatusText(meeting.status)}
                    </div>
                    
                    {meeting.status === 'pending' && (
                      // Eğer buyer_id var ama user_id company admin değilse, bu buyer'ın standdan istediği meeting, stand admini onaylayabilir
                      // Eğer buyer_id var ve user_id company admin ise, bu stand admin'in buyer'a istediği meeting, buyer onaylamalı
                      (meeting.buyer_id && meeting.user_id === user?.id) ? (
                        // Stand admin buyer'a meeting istemiş, buyer onaylamalı
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#ffc107',
                          color: '#333',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Waiting for buyer approval
                        </div>
                      ) : (
                        // Normal user'dan gelen veya buyer'ın standdan istediği meeting, stand admini onaylayabilir
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={() => handleUpdateStatus(meeting.id, 'approved')}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(meeting.id, 'declined')}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Sent Meetings (to buyers) */}
        <div>
          <h2>Sent Meeting Requests</h2>
          {(() => {
            // Gönderilen toplantılar: user_id = stand admininin user_id
            // Ama kendi standına gönderilen meetingler "Sent" değil, "Incoming" olmalı
            // (Buyer'a gönderilen veya başka standa gönderilen meetingler)
            // Tip uyumsuzluğunu önlemek için string'e çevir
            const sentMeetings = meetings.filter(meeting => {
              const meetingUserId = String(meeting.user_id);
              const currentUserId = String(user?.id);
              const meetingCompanyId = String(meeting.company_id);
              const currentCompanyId = String(companyInfo?.id || '');
              
              // Stand admininin gönderdiği meetingler ama kendi standına değil
              // (Buyer'a gönderilen VEYA başka standa gönderilen)
              return meetingUserId === currentUserId && 
                     (meeting.buyer_id || (meetingCompanyId !== currentCompanyId && currentCompanyId !== ''));
            });
            return sentMeetings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No sent meeting requests yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {sentMeetings.map((meeting) => (
                  <div
                    key={meeting.id}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '20px',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        {/* Eğer buyer'a gönderilmişse buyer bilgileri, başka standa gönderilmişse company bilgileri */}
                        {meeting.buyer_id ? (
                          <>
                            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                              {meeting.buyer?.name || meeting.requester_name || 'Unknown'} {meeting.buyer?.surname || meeting.requester_surname || ''}
                            </h4>
                            <p style={{ margin: '5px 0', color: '#666' }}>
                              <strong>Email:</strong> {meeting.buyer?.email || meeting.requester_email || 'N/A'}
                            </p>
                            <p style={{ margin: '5px 0', color: '#666' }}>
                              <strong>Phone:</strong> {meeting.buyer?.phone || 'N/A'}
                            </p>
                          </>
                        ) : (
                          <>
                            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                              {meeting.company?.name || 'Unknown Company'}
                            </h4>
                            {meeting.company?.admin && (
                              <>
                                <p style={{ margin: '5px 0', color: '#666' }}>
                                  <strong>Contact:</strong> {meeting.company.admin.name || 'N/A'}
                                </p>
                                <p style={{ margin: '5px 0', color: '#666' }}>
                                  <strong>Email:</strong> {meeting.company.admin.email || meeting.company.email || 'N/A'}
                                </p>
                                <p style={{ margin: '5px 0', color: '#666' }}>
                                  <strong>Phone:</strong> {meeting.company.admin.phone || meeting.company.phone || 'N/A'}
                                </p>
                              </>
                            )}
                          </>
                        )}
                        <p style={{ margin: '5px 0', color: '#666' }}>
                          <strong>Date:</strong> {formatDate(meeting.meeting_slot.meeting_date)}
                        </p>
                        <p style={{ margin: '5px 0', color: '#666' }}>
                          <strong>Time:</strong> {formatTime(meeting.meeting_slot.start_time)} - {formatTime(meeting.meeting_slot.end_time)}
                        </p>
                      </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                        <div
                          style={{
                            backgroundColor: getStatusColor(meeting.status),
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {getStatusText(meeting.status)}
                        </div>
                        
                        {meeting.status === 'pending' && (
                          <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#ffc107',
                            color: '#333',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {meeting.buyer_id ? 'Waiting for buyer approval' : 'Waiting for company admin approval'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
      )}

      {activeTab === 'slots' && (
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>Manage Time Slots</h2>
            <button
              onClick={fetchSlots}
              style={{
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          </div>

          {slotsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading slots...
            </div>
          ) : slots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No slots found.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Time</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Has Meeting</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((slot) => {
                    const hasActiveMeeting = slot.meetings && slot.meetings.length > 0;
                    return (
                      <tr key={slot.id} style={{ backgroundColor: slot.is_available ? '#fff' : '#fff3cd' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          {formatDate(slot.meeting_date)}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: slot.is_available ? '#d4edda' : '#f8d7da',
                            color: slot.is_available ? '#155724' : '#721c24',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {slot.is_available ? 'Available' : 'Closed'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          {hasActiveMeeting ? (
                            <span style={{ color: '#28a745', fontWeight: 'bold' }}>Yes</span>
                          ) : (
                            <span style={{ color: '#666' }}>No</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          <button
                            onClick={() => handleToggleSlot(slot.id, slot.is_available)}
                            disabled={!slot.is_available && hasActiveMeeting}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: slot.is_available ? '#dc3545' : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: (!slot.is_available && hasActiveMeeting) ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              opacity: (!slot.is_available && hasActiveMeeting) ? 0.5 : 1
                            }}
                            title={(!slot.is_available && hasActiveMeeting) ? 'Cancel the meeting first to open this slot' : ''}
                          >
                            {slot.is_available ? 'Close Slot' : 'Open Slot'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'buyers' && (
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>Buyers</h2>
            <button
              onClick={fetchBuyers}
              style={{
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          </div>

          {buyersLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading buyers...
            </div>
          ) : buyers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No buyers found.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
              {buyers.map((buyer) => (
                <div
                  key={buyer.id}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                    {buyer.name} {buyer.surname}
                  </h4>
                  {buyer.email && (
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Email:</strong> {buyer.email}
                    </p>
                  )}
                  {buyer.phone && (
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Phone:</strong> {buyer.phone}
                    </p>
                  )}
                  {buyer.company_name && (
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      <strong>Company:</strong> {buyer.company_name}
                    </p>
                  )}
                  {buyer.notes && (
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '0.9rem' }}>
                      <strong>Notes:</strong> {buyer.notes}
                    </p>
                  )}
                  <button
                    onClick={() => openRequestModal(buyer)}
                    style={{
                      marginTop: '15px',
                      padding: '8px 16px',
                      backgroundColor: '#EDB404',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      width: '100%',
                      fontWeight: 'bold'
                    }}
                  >
                    Request Meeting
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'company-info' && (
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2>Company Information</h2>
          
          {companyInfo && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <p><strong>Company Name:</strong> {companyInfo.name}</p>
              <p><strong>Email:</strong> {companyInfo.email}</p>
            </div>
          )}

          <form onSubmit={handleUpdateDescriptions}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Short Description 1 (max 255 characters)
              </label>
              <input
                type="text"
                value={descriptionData.description_1}
                onChange={(e) => setDescriptionData({ ...descriptionData, description_1: e.target.value })}
                maxLength={255}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="Enter first description"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Short Description 2 (max 255 characters)
              </label>
              <input
                type="text"
                value={descriptionData.description_2}
                onChange={(e) => setDescriptionData({ ...descriptionData, description_2: e.target.value })}
                maxLength={255}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="Enter second description"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Short Description 3 (max 255 characters)
              </label>
              <input
                type="text"
                value={descriptionData.description_3}
                onChange={(e) => setDescriptionData({ ...descriptionData, description_3: e.target.value })}
                maxLength={255}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="Enter third description"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Long Description (300-400 characters)
              </label>
              <textarea
                value={descriptionData.company_description}
                onChange={(e) => setDescriptionData({ ...descriptionData, company_description: e.target.value })}
                maxLength={400}
                rows={6}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Enter detailed company description (300-400 characters)"
              />
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                {descriptionData.company_description.length} / 400 characters
              </div>
            </div>

            <button
              type="submit"
              disabled={updating}
              style={{
                padding: '12px 24px',
                backgroundColor: updating ? '#ccc' : '#EDB404',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: updating ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              {updating ? 'Updating...' : 'Update Descriptions'}
            </button>
          </form>
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default CompanyAdminDashboard;
